import { useMutation, useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'

import { Button } from '@/components/elements/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/elements/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import PasswordInput from '@/components/PasswordInput/PasswordInput'
import { entityRoleSetPassword } from '@/services/entity.service'
import { toast } from '@/utils/helpers'
import { formatUserName } from '@/utils/userHelpers'

const SetPasswordDialog = ({ user, entityId, roleId, open, setOpen, queryKey }) => {
  const form = useForm({
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  })

  const queryClient = useQueryClient()

  const { isPending, mutate: setPassword } = useMutation({
    mutationFn: (data) => entityRoleSetPassword(data),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: () => {
      toast('Password updated successfully. User has been notified.', 'success')
      setOpen(false)
      form.reset()
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const onSubmit = (data) => {
    if (data.newPassword !== data.confirmPassword) {
      form.setError('confirmPassword', {
        type: 'manual',
        message: 'Passwords do not match',
      })
      return
    }

    setPassword({
      entityId,
      roleId,
      newPassword: data.newPassword,
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent setOpen={setOpen}>
        <DialogHeader>
          <DialogTitle>Set Password</DialogTitle>
          <DialogDescription>
            Set a new password for {formatUserName(user)}. The user will be notified via email.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <FormField
              control={form.control}
              disabled={isPending}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Password</FormLabel>
                  <FormControl>
                    <PasswordInput id="newPassword" placeholder="Enter new password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{
                required: 'This field is required',
                minLength: {
                  value: 8,
                  message: 'Password must be at least 8 characters',
                },
              }}
            />

            <FormField
              control={form.control}
              disabled={isPending}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      id="confirmPassword"
                      placeholder="Confirm new password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{
                required: 'This field is required',
              }}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setOpen(false)
                  form.reset()
                }}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" loading={isPending}>
                Set Password
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

SetPasswordDialog.propTypes = {
  user: PropTypes.object.isRequired,
  entityId: PropTypes.string.isRequired,
  roleId: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  queryKey: PropTypes.array.isRequired,
}

export default SetPasswordDialog
